import React, { useState, useEffect } from 'react';
import { Settings, DollarSign } from 'lucide-react';
import axios from 'axios';

const RatesTab = ({
  commissionRates,
  setCommissionRates,
  loading,
  setLoading,
  language,
  t,
  showSuccessToast,
  showErrorToast,
  loadData
}) => {
  const [ilsRate, setIlsRate] = useState(3.6);
  const [ilsLoading, setIlsLoading] = useState(false);

  useEffect(() => {
    loadILSRate();
  }, []);

  const loadILSRate = async () => {
    try {
      const response = await axios.get('/api/vendor/admin/ils-rate');
      setIlsRate(response.data.ilsToUsdRate);
    } catch (error) {
      console.error('Error loading ILS rate:', error);
    }
  };

  const updateILSRate = async () => {
    setIlsLoading(true);
    try {
      await axios.put('/api/vendor/admin/ils-rate', { rate: ilsRate });
      showSuccessToast(t('ilsRateUpdated', language) || 'ILS rate updated successfully');
      loadILSRate();
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Failed to update ILS rate');
    } finally {
      setIlsLoading(false);
    }
  };

  const updateCommissionRates = async () => {
    setLoading(true);
    try {
      await axios.post('/api/admin/commission-rates', commissionRates);
      showSuccessToast(t('commissionRatesUpdated', language));
      loadData();
    } catch (error) {
      showErrorToast(error.response?.data?.message || t('failedToUpdateCommissionRates', language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="role-management">
      {/* ILS Rate Section */}
      <div className="role-management-section" style={{ marginBottom: '24px' }}>
        <h3 className="role-management-title">
          <DollarSign className="w-5 h-5" />
          {t('vendorILSRate', language) || 'Vendor ILS Rate'}
        </h3>
        <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
          {t('ilsRateDescription', language) || 'Exchange rate used for vendor package pricing (ILS to USD)'}
        </p>
        <div className="role-select-group" style={{ maxWidth: '300px' }}>
          <label className="role-select-label">
            {t('ilsToUsdRate', language) || '1 USD = ? ILS'}
          </label>
          <input
            type="number"
            value={ilsRate}
            onChange={(e) => setIlsRate(parseFloat(e.target.value) || 0)}
            className="role-search-input"
            min="1"
            max="10"
            step="0.01"
            placeholder="3.60"
          />
          <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
            Example: If rate is 3.60, then 100 ILS = ${(100 / ilsRate).toFixed(2)} USD
          </p>
        </div>
        <button
          onClick={updateILSRate}
          disabled={ilsLoading}
          className="role-change-btn"
          style={{ marginTop: '16px' }}
        >
          {ilsLoading ? (t('updating', language) || 'Updating...') : (t('updateILSRate', language) || 'Update ILS Rate')}
        </button>
      </div>

      {/* Commission Rates Section */}
      <div className="role-management-section">
        <h3 className="role-management-title">
          <Settings className="w-5 h-5" />
          {t('commissionRateSettings', language)}
        </h3>
        <div className="role-management-grid">
          {['multiplayer', 'battleRoyale'].map(gameMode => (
            <div key={gameMode} className="role-search-section">
              <h4 className="role-management-title">
                {gameMode === 'multiplayer' ? t('multiplayerTournaments', language) : t('battleRoyaleTournaments', language)}
              </h4>
              {['approver', 'system'].map(type => (
                <div key={type} className="role-select-group">
                  <label className="role-select-label">
                    {type === 'approver' ? t('approverCommission', language) : t('systemCommission', language)}
                  </label>
                  <input
                    type="number"
                    value={commissionRates[gameMode][type]}
                    onChange={(e) => setCommissionRates({
                      ...commissionRates,
                      [gameMode]: {
                        ...commissionRates[gameMode],
                        [type]: parseFloat(e.target.value) || 0
                      }
                    })}
                    className="role-search-input"
                    min="0"
                    max="50"
                    step="0.5"
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        <button
          onClick={updateCommissionRates}
          disabled={loading}
          className="role-change-btn"
        >
          {loading ? t('updating', language) : t('updateCommissionRates', language)}
        </button>
      </div>
    </div>
  );
};

export default RatesTab;
